<template>
  <div
    class="d-flex justify-content-between align-items-start mb-1"
    style="gap: 1rem"
  >
    <div
      v-if="hasPermission($store.state.user, 'DISBURSEMENTS_WRITE')"
      class="d-flex flex-wrap"
      style="gap: 0.5rem; flex: 1"
    >
      <!-- Processed -->
      <template v-if="processed">
        <button
          class="btn btn-warning btn-sm"
          @click="unprocessedAllDisbursement"
        >
          <i class="fa fa-exclamation-triangle"></i> Annuler les traitements
          (passer en attente)
        </button>
        <button class="btn btn-success btn-sm" @click="payAll">
          <i class="fa fa-check-circle"></i> Marquer les factures séléctionnées
          comme payées
        </button>
        <button
          v-if="selected.length > 1"
          class="btn btn-primary btn-sm"
          @click="xmlBankingFile('company')"
        >
          <i class="fa fa-cloud-download"></i> XML COMP
        </button>
        <button
          v-if="selected.length > 1"
          class="btn btn-primary btn-sm"
          @click="xmlBankingFile('corporate')"
        >
          <i class="fa fa-cloud-download"></i> XML CORP
        </button>
      </template>

      <!-- Payed -->
      <template v-else-if="payed">
        <button
          class="btn btn-warning btn-sm"
          @click="unprocessedAllDisbursement"
        >
          <i class="fa fa-exclamation-triangle"></i> Annuler (passer en attente)
        </button>
        <button
          class="btn btn-outline-danger btn-sm"
          @click="unpayedAllDisbursement"
        >
          <i class="fa fa-exclamation-triangle"></i> Annuler les paiements
          (passer en traité)
        </button>
        <button class="btn btn-info btn-sm" @click="archivedAll">
          <i class="fa fa-check-circle"></i> Archiver les factures séléctionnées
        </button>
      </template>

      <!-- Blocked -->
      <template v-else-if="blocked">
        <button
          class="btn btn-warning btn-sm"
          @click="unprocessedAllDisbursement"
        >
          <i class="fa fa-check-circle"></i> Annuler le refus (passer "En
          attente")
        </button>
        <button class="btn btn-info btn-sm" @click="archivedAll">
          <i class="fa fa-check-circle"></i> Archiver les factures
        </button>
      </template>

      <!-- Not processed -->
      <template v-else>
        <button
          class="btn btn-success btn-sm"
          @click="processedAllDisbursement"
        >
          <i class="fa fa-check-circle"></i> Factures traitées
        </button>
        <button
          v-if="selected.length > 1 && $route.name !== 'user-details'"
          class="btn btn-primary btn-sm"
          @click="sendEmails"
        >
          <i class="fa fa-envelope"></i> Emails demande IBAN
        </button>
      </template>
    </div>

    <!-- Global Actions -->
    <div class="d-flex flex-wrap justify-content-end" style="gap: 0.5rem">
      <button
        v-if="selected.length > 1"
        class="btn btn-default btn-sm"
        :disabled="isDownloadingZip"
        @click="downloadInvoicesZip"
      >
        <i class="fa fa-cloud-download"></i> Télécharger un zip
      </button>
      <button
        v-if="selected.length > 1"
        class="btn btn-warning btn-sm"
        :disabled="isDownloadingMergedPdf"
        @click="downloadInvoicesMergedPdf"
      >
        <i class="fa fa-cloud-download"></i> Télécharger un pdf combiné
      </button>
    </div>
  </div>
</template>

<script>
import DownloadFile from "@/mixin/DownloadFile";

export default {
  props: {
    selected: {
      type: Array,
      required: false,
      default: () => [],
    },
    processed: {
      type: Boolean,
      required: false,
      default: false,
    },
    payed: {
      type: Boolean,
      required: false,
      default: false,
    },
    blocked: {
      type: Boolean,
      required: false,
      default: false,
    },
    begin: {
      type: [Date, String],
      required: false,
      default: null,
    },
    end: {
      type: [Date, String],
      required: false,
      default: null,
    },
  },
  mixins: [DownloadFile],
  data() {
    return {
      isDownloadingZip: false,
      isDownloadingMergedPdf: false,
    };
  },
  methods: {
    async downloadInvoicesZip() {
      try {
        this.isDownloadingZip = true;

        const invoiceIds = this.selected
          .filter((d) => !!d.invoice)
          .map((d) => d.invoice);

        const { data } = await this.$api.post("/uploads/download", {
          invoiceIds,
        });

        this.downloadSecuredFile(`${data.archive}`);
      } catch (e) {
        this.$errorToast("Impossible de télécharger le zip des factures");
      } finally {
        this.isDownloadingZip = false;
      }
    },

    async downloadInvoicesMergedPdf() {
      try {
        this.isDownloadingMergedPdf = true;

        let type = "not-processed";

        if (this.processed) {
          type = "processed";
        } else if (this.payed) {
          type = "payed";
        } else if (this.blocked) {
          type = "blocked";
        }

        const { data } = await this.$api.post(
          "/disbursements/download-merged-pdf",
          {
            disbursementIds: this.selected.map((d) => d.id),
            type,
            filterBegin: this.begin,
            filterEnd: this.end,
          },
        );

        this.downloadSecuredFile(data.archive);
      } catch (e) {
        this.$errorToast(
          "Impossible de télécharger le PDF combiné des factures",
        );
      } finally {
        this.isDownloadingMergedPdf = false;
      }
    },

    xmlBankingFile(type) {
      this.$confirm({
        message: "Créer un fichier XML pour le transfert bancaire ?",
        onConfirm: async () => {
          try {
            const { data } = await this.$api.post(
              `/disbursements/xml-banking-export`,
              {
                disbursementIds: this.selected.map((d) => d.id),
                type,
              },
            );

            await this.downloadSecuredFile(`${data}`);

            window.location.reload();
          } catch (e) {
            console.warn(e);
          }
        },
      });
    },

    processedAllDisbursement() {
      this.$confirm({
        message:
          "Marquer toutes les factures comme traitées ? (Seulement si l'iban est présent)",
        onConfirm: async () => {
          const selectedWithIban = this.selected.filter(
            (d) => !!d.coach?.coach?.iban,
          );

          for (const { id } of selectedWithIban) {
            this.$emit("process", id);
          }
        },
      });
    },

    unprocessedAllDisbursement() {
      this.$confirm({
        message: `Rebasculer toutes les factures dans la section "En attente" ?`,
        onConfirm: async () => {
          for (const { id } of this.selected) {
            this.$emit("unprocess", id);
          }
        },
      });
    },

    payAll() {
      this.$confirm({
        message: "Marquer toutes les factures comme payées ?",
        onConfirm: async () => {
          for (const { id } of this.selected) {
            this.$emit("manual-pay", id);
          }
        },
      });
    },

    unpayedAllDisbursement() {
      this.$confirm({
        message: "Annuler le paiement de toutes les factures séléctionnées ?",
        onConfirm: async () => {
          for (const { id } of this.selected) {
            this.$emit("unpay", id);
          }
        },
      });
    },

    archivedAll() {
      this.$confirm({
        message:
          "Archiver toutes les factures ? Cette action est irréversible.",
        onConfirm: async () => {
          for (const { id } of this.selected) {
            this.$emit("archive", id);
          }
        },
      });
    },

    sendEmails() {
      this.$confirm({
        message:
          "Envoyer des emails de demande d'IBAN ? (les utilisateurs disposants déjà d'un IBAN ne seront pas impactés)",
        onConfirm: async () => {
          for (const { coach } of this.selected) {
            this.$emit("iban", coach);
          }
        },
      });
    },
  },
};
</script>
